import { AbstractControl, FormArray, ValidationErrors, ValidatorFn } from '@angular/forms';
import moment from 'moment';
import { LeaveType } from 'src/app/pages/requests/pages/leave-request/models';
import { Constants } from '../constants';
import { DayOfWeek, OperatingHours, OperatingHoursValidatorConfig } from '../models';

export function matchValidator(matchTo: string, reverse?: boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.parent && reverse) {
      const c = (control.parent?.controls as any)[matchTo] as AbstractControl;
      if (c) {
        c.updateValueAndValidity();
      }
      return null;
    }
    return !!control.parent && !!control.parent.value && control.value === (control.parent?.controls as any)[matchTo].value ? null : { misMatch: true };
  };
}

const TIME_PARSE_FORMATS = [moment.ISO_8601, 'YYYY-MM-DDTHH:mm:ss', 'HH:mm:ss', 'HH:mm', 'h:mm A', 'h:mm a', 'hh:mm A', 'hh:mm a'];

export function parseTimeToMoment(value: string): moment.Moment {
  return moment(value, TIME_PARSE_FORMATS, false);
}

export function timeRangeValidator(startControlName: string, endControlName: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const startControl = control.get(startControlName);
    const endControl = control.get(endControlName);

    if (!startControl || !endControl) {
      return null;
    }

    const start = startControl.value;
    const end = endControl.value;

    if (!start || !end) {
      return null;
    }

    const startMoment = parseTimeToMoment(start);
    const endMoment = parseTimeToMoment(end);

    if (startMoment.isValid() && endMoment.isValid() && !endMoment.isAfter(startMoment)) {
      endControl.setErrors({ timeRangeInvalid: true });
      return { timeRangeInvalid: true };
    } else {
      if (endControl.hasError('timeRangeInvalid')) {
        endControl.setErrors(null);
      }
    }

    return null;
  };
}

function toggleError(control: AbstractControl, key: string, active: boolean): void {
  const errors = { ...(control.errors ?? {}) };

  if (active) {
    control.setErrors({ ...errors, [key]: true });
    return;
  }

  if (errors[key]) {
    delete errors[key];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }
}

export function slotOverlapValidator(dayControlName: string, startControlName: string, endControlName: string, slotsControlName?: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const entries = (control as FormArray).controls ?? [];
    const slots = slotsControlName ? entries.flatMap((entry) => (entry.get(slotsControlName) as FormArray | null)?.controls ?? []) : entries;

    const ranges = slots.map((slot) => {
      const day = slot.get(dayControlName)?.value;
      const start = parseTimeToMoment(slot.get(startControlName)?.value ?? '');
      const end = parseTimeToMoment(slot.get(endControlName)?.value ?? '');
      const hasDay = day !== undefined && day !== null && day !== '';
      return hasDay && start.isValid() && end.isValid() ? { day: Number(day), start: start.valueOf(), end: end.valueOf() } : undefined;
    });

    let hasOverlap = false;
    ranges.forEach((range, index) => {
      const overlaps = !!range && ranges.some((other, i) => i < index && other?.day === range.day && other.start < range.end && range.start < other.end);
      const startControl = slots[index].get(startControlName);
      if (startControl) {
        toggleError(startControl, 'slotOverlap', overlaps);
      }
      hasOverlap = hasOverlap || overlaps;
    });

    return hasOverlap ? { slotOverlap: true } : null;
  };
}

export function phoneNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }

    return value.replace(/\D/g, '').length === 10 ? null : { phoneNumberInvalid: true };
  };
}

export function outOfRangeTimeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const parsed = parseTimeToMoment(control.value);
    if (!parsed.isValid()) return { outOfRangeTime: true };

    const timeStr = parsed.format(Constants.dateFormats.HH_mm_ss);
    const isValid = timeStr >= Constants.minScheduleTime && timeStr <= Constants.maxScheduleTime;

    return isValid ? null : { outOfRangeTime: true };
  };
}

function formatOperatingHours(hours: OperatingHours): string {
  const format = (time: string) => parseTimeToMoment(time).format('h:mm A');
  return `${format(hours.start)} - ${format(hours.end)}`;
}

function isTimeOutsideHours(value: string, hours: OperatingHours): boolean {
  const parsed = parseTimeToMoment(value);
  if (!parsed.isValid()) {
    return false;
  }
  const timeStr = parsed.format(Constants.dateFormats.HH_mm_ss);
  return timeStr < hours.start || timeStr > hours.end;
}

function applyOutsideHoursError(control: AbstractControl, hours: OperatingHours | undefined): void {
  const errors = { ...(control.errors ?? {}) };

  if (hours && control.value && isTimeOutsideHours(control.value, hours)) {
    control.setErrors({ ...errors, outsideOperatingHours: { range: formatOperatingHours(hours) } });
    return;
  }

  if (errors['outsideOperatingHours']) {
    delete errors['outsideOperatingHours'];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }
}

function resolveDayOfWeek(value: unknown, deriveFromDate?: boolean): DayOfWeek | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (deriveFromDate) {
    const parsed = moment(value as moment.MomentInput);
    return parsed.isValid() ? (parsed.day() as DayOfWeek) : undefined;
  }
  const day = Number(value);
  return Number.isInteger(day) && day >= 0 && day <= 6 ? (day as DayOfWeek) : undefined;
}

export function operatingHoursValidator(config: OperatingHoursValidatorConfig): ValidatorFn {
  const hoursMap = config.hours ?? Constants.locationOperatingHours;
  return (control: AbstractControl): ValidationErrors | null => {
    const dayControl = control.get(config.dayControlName);
    if (!dayControl) {
      return null;
    }

    const day = resolveDayOfWeek(dayControl.value, config.deriveDayFromDate);
    const hours = day === undefined ? undefined : hoursMap[day];
    let hasError = false;

    for (const name of config.timeControlNames) {
      const timeControl = control.get(name);
      if (!timeControl) {
        continue;
      }

      applyOutsideHoursError(timeControl, hours);
      if (timeControl.hasError('outsideOperatingHours')) {
        hasError = true;
      }
    }

    return hasError ? { outsideOperatingHours: true } : null;
  };
}

export function dateOfBirthValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const dateOfBirth = control.value;
    if (!dateOfBirth) {
      return null;
    }

    const birthDate = moment(dateOfBirth);
    const age = moment().diff(birthDate, 'years');
    return age >= Constants.requiredAge ? null : { minimumAge: true };
  };
}

export function zipCodeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }

    // Check if zipcode is a 5-digit number between 00501 and 99950 (valid US zip codes)
    const numericValue = parseInt(value, 10);
    const isValid = /^\d+$/.test(value) && !isNaN(numericValue) && numericValue >= 501 && numericValue <= 999999 && value.toString().length < 7;

    return isValid ? null : { zipCodeInvalid: true };
  };
}

export function usedLeaveDaysValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control.parent;
    if (!formGroup) return null;

    const leaveType = formGroup.get('leaveType')?.value;
    const totalLeaveDays = formGroup.get('totalLeaveDays')?.value;
    const usedLeaveDays = control.value;

    // Only validate for paid leave (type 1)
    if (leaveType === LeaveType.PAID && usedLeaveDays > totalLeaveDays) {
      return { usedDaysExceeded: true };
    } else {
      // Clear the error if it exists but condition is no longer true
      if (control.hasError('usedDaysExceeded')) {
        control.setErrors(null);
      }
    }

    return null;
  };
}

export function noWhitespaceValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }
    const str = value as string;
    return str.trim().length === 0 || str !== str.trim() ? { whitespace: true } : null;
  };
}

export function minthirtyValidator(startControlName: string, endControlName: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const startControl = control.get(startControlName);
    const endControl = control.get(endControlName);

    if (!startControl || !endControl) {
      return null;
    }

    const start = moment(startControl.value);
    const end = moment(endControl.value);
    const diff = end.diff(start, 'minutes');
    const isValid = diff >= 30;

    if (start && end && !isValid) {
      endControl.setErrors({ minLeaveDuration: true });
      return { minLeaveDuration: true };
    } else {
      if (endControl.hasError('minLeaveDuration')) {
        endControl.setErrors(null);
      }
    }

    return null;
  };
}

export function ageGroupDobValidator(ageGroup: number): ValidatorFn {
  const ageRanges: Record<number, [number, number]> = {
    1: [2.5, 6],
    2: [6, 9],
    3: [9, 18],
    4: [18, Infinity]
  };

  return (control: AbstractControl): ValidationErrors | null => {
    const range = ageRanges[ageGroup];
    if (!control.value || !range) {
      return null;
    }

    const [minAge, maxAge] = range;
    const age = moment().diff(moment(control.value), 'months') / 12;
    return age >= minAge && age < maxAge ? null : { ageGroupMismatch: { label: Constants.ageOptions.find((option) => option.value === ageGroup)?.label } };
  };
}
